import { http } from '../http';
import { extrairMensagemErro } from '../../utils/apiError';

/**
 * Rascunho de adesão (at-38, item 22).
 *
 * O fluxo de adesão gravava tudo só no Redux persistido (por navegador) e o
 * `performLogout` limpava esse estado — trocar de aparelho, limpar o navegador
 * ou ser deslogado no meio do caminho significava perder a adesão inteira.
 * Estas rotas guardam o snapshot no banco, escopado ao consultor logado.
 *
 * Usa o cliente `http` central (at-17): o Bearer é injetado pelo interceptor,
 * nunca montado aqui.
 *
 * ⚠️ As funções deste módulo **lançam**. Quem engole erro é a camada de fluxo
 * (`utils/adesaoFluxo.ts`), de propósito: a lista de rascunhos precisa ver a
 * falha, o fluxo de adesão não pode ser interrompido por ela (D8).
 */

/** Próxima tela a abrir. Não existe `ASSOCIADO`: o rascunho nasce ao concluí-la. */
export type EtapaRascunhoAdesao =
  | 'CARRO'
  | 'DOCUMENTO'
  | 'PRODUTO'
  | 'TERMO'
  | 'ASSINATURA';

export type StatusRascunhoAdesao = 'EM_ANDAMENTO' | 'CONCLUIDO' | 'DESCARTADO';

/** Filtro da listagem. `TODOS` é o "mostrar histórico" da tela. */
export type FiltroStatusRascunho = StatusRascunhoAdesao | 'TODOS';

/**
 * Snapshot dos slices do frontend (versão 1).
 *
 * É o formato INTERNO dos slices de propósito: a reidratação é `dispatch`
 * direto, sem mapeamento banco → slice. O mapeamento seria o ponto frágil
 * (30+ campos, endereço duplicado na raiz do `associadoSlice`), e assim ele
 * simplesmente não existe. O backend trata o payload como opaco.
 *
 * ⚠️ Nunca entra aqui: token, qualquer coisa do slice `auth`, e base64 de
 * foto/documento (o rascunho em `DOCUMENTO` recaptura os documentos).
 */
export interface PayloadRascunhoAdesaoV1 {
  versao: 1;
  /** `associadoSlice` inteiro — inclui `carro` e os campos de endereço da raiz. */
  associado: Record<string, unknown>;
  /** `codigoSlice`: regional, cooperativa, tipo de veículo e valor FIPE do fluxo. */
  codigo: {
    codigoRegional?: string;
    codigoCooperativa?: string;
    codigoTipoVeiculo?: string;
    valorFipe?: string;
  };
  /** `respostasDoSgaSlice` — o que importa aqui é o `codigoTermo` da etapa TERMO. */
  respostasDoSga?: {
    codigoAssociado?: string | null;
    codigoVeiculo?: string | null;
    codigoEmissao?: string | null;
    codigoTermo?: string | null;
  };
}

export interface RascunhoAdesaoResumo {
  id: string;
  cpfCnpj: string;
  nomeCompleto: string;
  placa: string | null;
  etapa: EtapaRascunhoAdesao;
  status: StatusRascunhoAdesao;
  associadoId: string | null;
  carroId: string | null;
  criadoEm?: string | null;
  /** Campo do "Atualizado" da lista. Opcional por resiliência de contrato. */
  atualizadoEm?: string | null;
  concluidoEm?: string | null;
  descartadoEm?: string | null;
}

export interface RascunhoAdesaoCompleto extends RascunhoAdesaoResumo {
  /** `null` nos status terminais — a PII é zerada ao concluir/descartar (D4). */
  payload: PayloadRascunhoAdesaoV1 | null;
  /**
   * Existe `Associado` vivo com este CPF **e** o rascunho está em etapa
   * pré-cadastro: alguém cadastrou por fora enquanto o rascunho dormia.
   */
  cpfJaCadastrado: boolean;
}

export interface ListaRascunhosAdesao {
  totalItems: number;
  page: number;
  limit: number;
  items: RascunhoAdesaoResumo[];
  totais: { emAndamento: number };
}

/**
 * Erro carregando o status HTTP — a tela separa **409** (concluído/descartado
 * em outro aparelho) de falha genérica. Mesmo padrão do `ErroEnvioSga` (at-31)
 * e do `ErroImportacaoSga` (at-32).
 */
export interface ErroRascunhoAdesao extends Error {
  status: number | null;
}

const erroComStatus = (error: unknown, fallback: string): ErroRascunhoAdesao => {
  const err = new Error(extrairMensagemErro(error, fallback)) as ErroRascunhoAdesao;
  err.status = (error as any)?.response?.status ?? null;
  return err;
};

export interface CriarRascunhoInput {
  cpfCnpj: string;
  nomeCompleto: string;
  placa?: string;
  etapa: EtapaRascunhoAdesao;
  payload: PayloadRascunhoAdesaoV1;
}

export interface AtualizarRascunhoInput {
  etapa?: EtapaRascunhoAdesao;
  payload?: PayloadRascunhoAdesaoV1;
  nomeCompleto?: string;
  placa?: string;
  associadoId?: string;
  carroId?: string;
}

export interface ListarRascunhosParams {
  page?: number;
  limit?: number;
  busca?: string;
  status?: FiltroStatusRascunho;
}

/**
 * Cria o rascunho — ou atualiza o que já existe para o mesmo CPF (D6: o
 * backend responde 200 com o existente em vez de 409/duplicata).
 */
export const criarRascunho = async (
  input: CriarRascunhoInput,
): Promise<RascunhoAdesaoResumo> => {
  try {
    const { data } = await http.post<RascunhoAdesaoResumo>('/rascunhos-adesao', input);
    return data;
  } catch (error) {
    throw erroComStatus(error, 'Não foi possível salvar o rascunho da adesão.');
  }
};

/** Avança a etapa / regrava o snapshot. Só vale em `EM_ANDAMENTO` (senão 409). */
export const atualizarRascunho = async (
  id: string,
  input: AtualizarRascunhoInput,
): Promise<RascunhoAdesaoResumo> => {
  try {
    const { data } = await http.patch<RascunhoAdesaoResumo>(
      `/rascunhos-adesao/${id}`,
      input,
    );
    return data;
  } catch (error) {
    throw erroComStatus(error, 'Não foi possível salvar o rascunho da adesão.');
  }
};

export const listarRascunhos = async (
  params: ListarRascunhosParams = {},
): Promise<ListaRascunhosAdesao> => {
  try {
    const { data } = await http.get<ListaRascunhosAdesao>('/rascunhos-adesao', {
      params: {
        page: params.page,
        limit: params.limit,
        // string vazia iria como `busca=` e o backend trataria como filtro
        busca: params.busca ? params.busca : undefined,
        status: params.status,
      },
    });
    return data;
  } catch (error) {
    throw erroComStatus(error, 'Não foi possível carregar as adesões em andamento.');
  }
};

/** Rascunho completo, com o `payload` para reidratar o fluxo. */
export const obterRascunho = async (id: string): Promise<RascunhoAdesaoCompleto> => {
  try {
    const { data } = await http.get<RascunhoAdesaoCompleto>(`/rascunhos-adesao/${id}`);
    return data;
  } catch (error) {
    throw erroComStatus(error, 'Não foi possível abrir o rascunho.');
  }
};

/** `EM_ANDAMENTO → DESCARTADO`. Marca, nunca apaga (convenção do projeto). */
export const descartarRascunho = async (id: string): Promise<RascunhoAdesaoResumo> => {
  try {
    const { data } = await http.patch<RascunhoAdesaoResumo>(
      `/rascunhos-adesao/${id}/descartar`,
    );
    return data;
  } catch (error) {
    throw erroComStatus(error, 'Não foi possível descartar o rascunho.');
  }
};

/** `EM_ANDAMENTO → CONCLUIDO` (fim do `/assinatura`). Zera o payload no backend. */
export const concluirRascunho = async (id: string): Promise<RascunhoAdesaoResumo> => {
  try {
    const { data } = await http.patch<RascunhoAdesaoResumo>(
      `/rascunhos-adesao/${id}/concluir`,
    );
    return data;
  } catch (error) {
    throw erroComStatus(error, 'Não foi possível concluir o rascunho.');
  }
};
